<template>
  <v-card class="sales-history" elevation="0">
    <div class="sales-history__heading">
      <div>
        <h2>Sales History</h2>
        <p>Model purchases and inventory profit by {{ viewMode === 'month' ? 'calendar month' : 'operational period' }}.</p>
      </div>
      <div class="sales-history__filters">
        <v-btn-toggle v-model="viewMode" color="primary" density="compact" mandatory variant="outlined">
          <v-btn value="month">Month</v-btn>
          <v-btn value="period">Period</v-btn>
        </v-btn-toggle>
        <v-text-field
          v-if="viewMode === 'month'"
          v-model="selectedMonth"
          class="sales-history__period"
          density="compact"
          hide-details
          label="Month"
          type="month"
          variant="outlined"
        />
        <v-select
          v-else
          v-model="selectedPeriodId"
          :items="periods"
          class="sales-history__period"
          item-title="name"
          item-value="id"
          label="Period"
          density="compact"
          hide-details
          variant="outlined"
        />
      </div>
    </div>

    <div :class="['sales-summary', { 'sales-summary--no-cost': !showCosts }]">
      <div v-for="card in summaryCards" :key="card.label" class="sales-summary__card">
        <span>{{ card.label }}</span>
        <strong>{{ card.value }}</strong>
        <small>{{ card.helper }}</small>
      </div>
    </div>

    <div v-if="loading" class="sales-history__empty">
      <v-progress-circular indeterminate color="primary" />
    </div>
    <div v-else-if="!sales.length" class="sales-history__empty">
      <v-icon size="36" color="primary">mdi-cart-outline</v-icon>
      <strong>No inventory sales in this {{ viewMode === 'month' ? 'month' : 'period' }}</strong>
      <p>Approved model purchases will appear here automatically.</p>
    </div>
    <div v-else class="sales-history__table-wrap">
      <table class="sales-history__table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Model</th>
            <th>Stage Name</th>
            <th>Item</th>
            <th>Method</th>
            <th class="number-cell">Quantity</th>
            <th v-if="showCosts" class="number-cell">Cost</th>
            <th class="number-cell">Sale Price</th>
            <th v-if="showCosts" class="number-cell">Profit</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="sale in sales" :key="sale.id">
            <td>{{ formatDate(sale.sale_date) }}</td>
            <td>{{ sale.fullName || '—' }}</td>
            <td>{{ sale.stageName || '—' }}</td>
            <td><strong>{{ sale.item_name }}</strong></td>
            <td>{{ sale.payment_method || 'Model adjustment' }}</td>
            <td class="number-cell">{{ sale.quantity }}</td>
            <td v-if="showCosts" class="number-cell">{{ formatKnownMoney(sale.cost_total, sale.currency) }}</td>
            <td class="number-cell">{{ formatMoney(sale.sale_total, sale.currency) }}</td>
            <td v-if="showCosts" :class="['number-cell', 'profit-cell', { positive: Number(sale.profit) > 0 }]">
              {{ formatKnownMoney(sale.profit, sale.currency) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </v-card>
</template>

<script>
import secureAxios from '@/utils/secureAxios';

const emptySummary = () => ({ sales: 0, cost: 0, profit: 0, quantity: 0, missingCostSales: 0 });

export default {
  name: 'InventorySalesHistory',
  props: {
    refreshKey: { type: Number, default: 0 },
    showCosts: { type: Boolean, default: true },
  },
  data() {
    return {
      periods: [],
      viewMode: 'month',
      selectedMonth: '',
      selectedPeriodId: null,
      sales: [],
      summary: emptySummary(),
      loading: false,
    };
  },
  computed: {
    summaryCards() {
      const missing = Number(this.summary.missingCostSales || 0);
      const cards = [
        { label: 'Units Sold', value: Number(this.summary.quantity || 0), helper: 'Inventory units' },
        { label: 'Total Sales', value: this.formatMoney(this.summary.sales), helper: 'Amount charged' },
        { label: 'Known Cost', value: this.formatMoney(this.summary.cost), helper: missing ? `${missing} sale${missing === 1 ? '' : 's'} missing cost` : 'All costs recorded' },
        { label: 'Known Profit', value: this.formatMoney(this.summary.profit), helper: missing ? 'Excludes missing costs' : 'Sales minus cost' },
      ];
      return this.showCosts ? cards : cards.slice(0, 2);
    },
  },
  watch: {
    refreshKey() { this.loadSales(); },
    viewMode() { this.loadSales(); },
    selectedMonth() {
      if (this.viewMode === 'month') this.loadSales();
    },
    selectedPeriodId() {
      if (this.viewMode === 'period') this.loadSales();
    },
  },
  async mounted() {
    const now = new Date();
    this.selectedMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    await this.loadPeriods();
    await this.loadSales();
  },
  methods: {
    dateOnly(value) {
      if (!value) return null;
      const raw = String(value).slice(0, 10);
      const [year, month, day] = raw.split('-').map(Number);
      return year && month && day ? new Date(year, month - 1, day) : null;
    },
    currentPeriod() {
      if (!this.periods.length) return null;
      const today = new Date();
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const ordered = [...this.periods].sort((left, right) => (
        this.dateOnly(left.start_date) - this.dateOnly(right.start_date)
      ));
      return ordered.find((period, index) => {
        const start = this.dateOnly(period.start_date);
        const nextStart = this.dateOnly(ordered[index + 1]?.start_date);
        return start && todayOnly >= start && (!nextStart || todayOnly < nextStart);
      }) || ordered.find((period) => this.dateOnly(period.start_date) >= todayOnly)
        || ordered[ordered.length - 1];
    },
    monthBounds() {
      const match = /^(\d{4})-(\d{2})$/.exec(this.selectedMonth || '');
      if (!match) return null;
      const year = Number(match[1]);
      const month = Number(match[2]);
      if (month < 1 || month > 12) return null;
      const lastDay = new Date(year, month, 0).getDate();
      return {
        startDate: `${year}-${String(month).padStart(2, '0')}-01`,
        endDate: `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
      };
    },
    async loadPeriods() {
      try {
        const response = await secureAxios.get('/api/hours/periods');
        this.periods = response.data || [];
        this.selectedPeriodId = this.currentPeriod()?.id || this.periods[0]?.id || null;
      } catch {
        this.periods = [];
        this.selectedPeriodId = null;
      }
    },
    async loadSales() {
      const month = this.viewMode === 'month' ? this.monthBounds() : null;
      if (this.viewMode === 'month' && !month) return;
      this.loading = true;
      try {
        const response = await secureAxios.get('/api/inventory/sales-history', {
          params: this.viewMode === 'month'
            ? { start_date: month.startDate, end_date: month.endDate }
            : { period_id: this.selectedPeriodId || undefined },
        });
        this.sales = response.data?.rows || [];
        this.summary = response.data?.summary || emptySummary();
      } catch {
        this.sales = [];
        this.summary = emptySummary();
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      const date = this.dateOnly(value);
      return date ? new Intl.DateTimeFormat('en-CA', { dateStyle: 'medium' }).format(date) : '—';
    },
    formatMoney(value, currency = 'COP') {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency', currency: currency || 'COP', maximumFractionDigits: 0,
      }).format(Number(value || 0));
    },
    formatKnownMoney(value, currency = 'COP') {
      return value === null || value === undefined ? 'Cost missing' : this.formatMoney(value, currency);
    },
  },
};
</script>

<style scoped>
.sales-history { border: 1px solid #dfe7f0; border-radius: 10px; overflow: hidden; }
.sales-history__heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 20px 22px; border-bottom: 1px solid #edf1f5; }
.sales-history h2 { margin: 0; color: #173553; font-size: 17px; }
.sales-history p { margin: 4px 0 0; color: #6b7f93; font-size: 13px; }
.sales-history__period { max-width: 260px; }
.sales-history__filters { display: flex; align-items: center; justify-content: flex-end; gap: 10px; }
.sales-history__filters .sales-history__period { width: 220px; }
.sales-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; padding: 16px 20px; background: #f8fafc; border-bottom: 1px solid #edf1f5; }
.sales-summary--no-cost { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.sales-summary__card { display: flex; min-width: 0; flex-direction: column; padding: 12px 14px; background: white; border: 1px solid #e5ebf1; border-radius: 8px; }
.sales-summary__card span { color: #6b7f93; font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; }
.sales-summary__card strong { margin-top: 3px; color: #173553; font-size: 19px; }
.sales-summary__card small { margin-top: 2px; color: #8795a4; font-size: 10px; }
.sales-history__empty { display: flex; min-height: 280px; flex-direction: column; align-items: center; justify-content: center; gap: 8px; text-align: center; }
.sales-history__table-wrap { overflow-x: auto; }
.sales-history__table { width: 100%; border-collapse: collapse; }
.sales-history__table th, .sales-history__table td { padding: 13px 14px; border-bottom: 1px solid #edf1f5; color: #40536a; font-size: 12px; text-align: left; white-space: nowrap; }
.sales-history__table th { background: #f8fafc; color: #6b7f93; font-size: 10px; letter-spacing: .06em; text-transform: uppercase; }
.number-cell { text-align: right !important; }
.profit-cell.positive { color: #27845b; font-weight: 700; }
@media (max-width: 800px) {
  .sales-history__heading { align-items: stretch; flex-direction: column; }
  .sales-history__period { max-width: none; }
  .sales-history__filters { align-items: stretch; flex-direction: column; }
  .sales-history__filters .sales-history__period { width: auto; }
  .sales-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
